import { useMemo } from 'react';
import merge from 'lodash/merge';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { useSettingsContext } from 'src/components/settings';

// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';
// options
import RTL from './options/right-to-left';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
import { createPresets } from './options/presets';
import { createContrast } from './options/contrast';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  modeOverride?: 'light' | 'dark';
};

export default function ThemeProvider({ children, modeOverride }: Props) {
  const settings = useSettingsContext();
  const themeMode = modeOverride ?? settings.themeMode;
  const { themeDirection, themeColorPresets, themeContrast } = settings;

  // The theme depends only on these four primitive settings. Building it is
  // expensive (createTheme + ~44 component-override factories + deep merges)
  // and a new theme object re-renders every mounted MUI/emotion consumer, so
  // it is rebuilt only when one of them actually changes — not whenever an
  // ancestor re-renders (e.g. a route navigation).
  const theme = useMemo(() => {
    const presets = createPresets(themeColorPresets);
    const contrast = createContrast(themeContrast, themeMode);

    const nextTheme = createTheme({
      palette: {
        ...palette(themeMode),
        ...presets.palette,
        ...contrast.palette,
      },
      customShadows: {
        ...customShadows(themeMode),
        ...presets.customShadows,
      },
      direction: themeDirection,
      shadows: shadows(themeMode),
      shape: { borderRadius: 8 },
      typography,
    } as ThemeOptions);

    nextTheme.components = merge(componentsOverrides(nextTheme), contrast.components);

    return nextTheme;
  }, [themeMode, themeDirection, themeColorPresets, themeContrast]);

  // MUI's ThemeProvider hands its consumers a fresh `{ ...theme, vars: null }`
  // on every render, so re-rendering it re-renders every styled component.
  // Settings-context updates that leave the theme inputs unchanged (e.g.
  // toggling the settings drawer) re-render this component with the same
  // `children`; reuse the provider subtree in that case.
  return useMemo(
    () => (
      <MuiThemeProvider theme={theme}>
        <RTL themeDirection={themeDirection}>
          <CssBaseline />
          {children}
        </RTL>
      </MuiThemeProvider>
    ),
    [theme, themeDirection, children]
  );
}
